import { Button, Flex, Loader, SelectField } from '@aws-amplify/ui-react';
import React, { useState } from 'react';
import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import { US_STATES, US_STATE_NAMES } from '../utils/UsStates';
import './ExportModal.scss';
import ExportStatusModal from './ExportStatusModal';

export type ExportFormat = 'CSV' | 'JSON';
export type TimeRange = '1_MONTH' | '3_MONTHS' | '6_MONTHS';

interface IngestionExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (format: ExportFormat, timeRange: TimeRange, stateFilter: string | null) => void;
  isExporting: boolean;
  exportResponse?: any;
  exportError?: string | null;
  siteId: string;
}

const IngestionExportModal: React.FC<IngestionExportModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isExporting,
  exportResponse,
  exportError,
  siteId
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('CSV');
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>('3_MONTHS');
  const [selectedState, setSelectedState] = useState<string>('');
  const [showExportHasStarted, setShowExportHasStarted] = useState(false);

  const handleExport = () => {
    onConfirm(selectedFormat, selectedTimeRange, selectedState || null);
  };
  
  const handleClose = () => {
    setShowExportHasStarted(false);
    // Reset selections to defaults when closing
    setSelectedFormat('CSV');
    setSelectedTimeRange('3_MONTHS');
    setSelectedState('');
    onClose();
  };
  
  const getTimeRangeLabel = (range: TimeRange): string => {
    switch (range) {
      case '1_MONTH': return '1 month';
      case '3_MONTHS': return '3 months';
      case '6_MONTHS': return '6 months';
      default: return '3 months';
    }
  };
  
  React.useEffect(() => {
    if (!isOpen) {
      setShowExportHasStarted(false);
      // Reset selections when modal is closed
      setSelectedFormat('CSV');
      setSelectedTimeRange('3_MONTHS');
      setSelectedState('');
    }
  }, [isOpen]);
  
  React.useEffect(() => {
    // When we get a jobId response, show the status modal
    if (exportResponse?.jobId) {
      setShowExportHasStarted(true);
    }
  }, [exportResponse]);
  
  return (
    <>
      {/* Export Status Modal - shown after we get jobId */}
      {showExportHasStarted && exportResponse?.jobId && (
        <ExportStatusModal
          isOpen={true}
          onClose={handleClose}
          exportId={exportResponse.jobId}
        />
      )}
      
      {/* Main Export Modal - stays open until we get jobId */}
      {!showExportHasStarted && (
        <ReactModal
          isOpen={isOpen}
          shouldCloseOnEsc={true}
          shouldCloseOnOverlayClick={true}
          onRequestClose={handleClose}
          style={General.getModalDefaults({ padding: '30px', width: '540px' })}
          closeTimeoutMS={200}
          parentSelector={General.getModalRoot}
        >
          <div>
            <h3 className="export-modal__title">
              Export Tracking Data
            </h3>
            
            {/* Export format selection */}
            <div className="export-modal__format-section">
              <div className="export-modal__format-label">Select export format</div>
              <Flex gap={4} wrap="nowrap">
                <button
                  className={`export-format-toggle ${selectedFormat === 'CSV' ? 'active' : ''}`}
                  onClick={() => setSelectedFormat('CSV')}
                  disabled={isExporting}
                >
                  CSV
                </button>
                
                <button
                  className={`export-format-toggle ${selectedFormat === 'JSON' ? 'active' : ''}`}
                  onClick={() => setSelectedFormat('JSON')}
                  disabled={isExporting}
                >
                  JSON
                </button>
              </Flex>
            </div>
            
            {/* Time range selection */}
            <div className="export-modal__format-section" style={{ marginTop: 20 }}>
              <div className="export-modal__format-label">Select time range</div>
              <Flex gap={4} wrap="nowrap">
                <button
                  className={`export-format-toggle ${selectedTimeRange === '1_MONTH' ? 'active' : ''}`}
                  onClick={() => setSelectedTimeRange('1_MONTH')}
                  disabled={isExporting}
                >
                  1 month
                </button>
                
                <button
                  className={`export-format-toggle ${selectedTimeRange === '3_MONTHS' ? 'active' : ''}`}
                  onClick={() => setSelectedTimeRange('3_MONTHS')}
                  disabled={isExporting}
                >
                  3 months
                </button>
                
                <button
                  className={`export-format-toggle ${selectedTimeRange === '6_MONTHS' ? 'active' : ''}`}
                  onClick={() => setSelectedTimeRange('6_MONTHS')}
                  disabled={isExporting}
                >
                  6 months
                </button>
              </Flex>
            </div>
            
            {/* State filter */}
            <div className="export-modal__format-section" style={{ marginTop: 20 }}>
              <div className="export-modal__format-label">Filter by state (optional)</div>
              <SelectField
                label=""
                labelHidden
                size="small"
                placeholder="All states"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                disabled={isExporting}
              >
                <option value="">All states</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {US_STATE_NAMES[state]} ({state})
                  </option>
                ))}
              </SelectField>
            </div>

            {/* Export info */}
            <div className="export-modal__info" style={{ marginTop: 20 }}>
              <div className="desc-2">
                This will export tracking data for the past {getTimeRangeLabel(selectedTimeRange)}{selectedState ? ` from ${US_STATE_NAMES[selectedState]}` : ''} in {selectedFormat} format.
              </div>
            </div>
            
            {/* Export button */}
            <Flex gap={15} justifyContent="flex-end" alignItems="center" className="export-modal__actions">
              {isExporting && (
                <Loader size="small" />
              )}
              <Button
                size="small"
                variation="primary"
                isDisabled={isExporting || !!exportResponse?.jobId}
                onClick={handleExport}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </Flex>
          </div>
        </ReactModal>
      )}
    </>
  );
};

export default IngestionExportModal;